import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@app/ui';
import { FadeIn, PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/deals')({
  validateSearch: z.object({
    status: z.string().optional(),
    search: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.deals.queryOptions({
        input: { status: deps.status, search: deps.search, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: AdminDeals,
});

function AdminDeals() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentSearch = search.search || '';
  const [searchInput, setSearchInput] = useState(currentSearch);

  // Sync local input with URL changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const { data } = useSuspenseQuery(
    api.admin.deals.queryOptions({
      input: { status: search.status, search: search.search, page: search.page },
    }),
  );

  const { deals, total, page, totalPages, statusCounts } = data;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== currentSearch) {
        navigate({ search: (prev) => ({ ...prev, search: searchInput || undefined, page: 1 }) });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, currentSearch, navigate]);

  return (
    <PageTransition>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">All Deals ({total})</h1>
        <div className="flex gap-2">
          <Select
            value={search.status || 'all'}
            onValueChange={(v) =>
              navigate({ search: { ...search, status: v === 'all' ? undefined : v, page: 1 } })
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="EXPIRED">Expired</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="search"
            placeholder="Search deals..."
            className="w-48"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <StaggerContainer className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StaggerItem>
          <SummaryCard label="Total Deals" value={String(statusCounts.all)} />
        </StaggerItem>
        <StaggerItem>
          <SummaryCard label="Active" value={String(statusCounts.active)} color="text-green-600" />
        </StaggerItem>
        <StaggerItem>
          <SummaryCard label="Draft" value={String(statusCounts.draft)} color="text-yellow-600" />
        </StaggerItem>
        <StaggerItem>
          <SummaryCard
            label="Completed"
            value={String(statusCounts.completed)}
            color="text-blue-600"
          />
        </StaggerItem>
        <StaggerItem>
          <SummaryCard
            label="Expired / Cancelled"
            value={String(statusCounts.expiredCancelled)}
            color="text-red-600"
          />
        </StaggerItem>
      </StaggerContainer>

      <FadeIn delay={0.3}>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Athlete</th>
                <th className="py-2 pr-4">Brand</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2">Deadline</th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No deals found.
                  </td>
                </tr>
              ) : (
                deals.map((deal) => (
                  <tr key={deal.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4">{deal.athleteName}</td>
                    <td className="py-2 pr-4">{deal.brandName}</td>
                    <td className="py-2 pr-4">{formatCurrency(deal.amount)}</td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={deal.status} />
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(deal.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {deal.endDate ? new Date(deal.endDate).toLocaleDateString() : '--'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}

function SummaryCard({
  label,
  value,
  color = 'text-foreground',
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`mt-1 text-xl font-semibold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
